import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Button } from "@heroui/react";
import { useFetchWithBody, usePost } from "../../api/reactQueryCustomHooks";
import { apiRoute, linksUrl } from "../../constants/enum";
import { ErrorCard, LoadingCard, MainContainer, NextUITable } from "../../components";
import { COLUMN_ALIGN_TYPE, COLUMN_TYPE, FOOTER_TYPE, ROW_ACTION_BTN_TYPE } from "../../components/NextUITable";
import { toast } from "react-toastify";
import ConfirmBox from "../../components/ConfirmBox";
import { setDetailParams, setNewEntryMode } from "../../state/outstandingBillEntrySlice";
import { calculateTotal } from "../../Utility/utility";

const OutstandingBills = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.auth);
    const { compCode } = useSelector((state) => state.company);

    // State for confirmation modal
    // const [selectedBill, setSelectedBill] = useState(null);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
     const [itemToDelete, setItemToDelete] = useState(null);

    // Fetch outstanding bills data
    const {
        data: billsData = [],
        isPending,
        isError,
        mutate: filterData
    } = usePost({
        url: apiRoute.transactionEntry.getOutstandingBills,
        queryKey: "fetchOutstandingBills",

    });
    const funLoadData = () => {
        filterData({
            data: {
                CompCode: compCode,

            }
        })
    }
    useEffect(() => {
        funLoadData();
    }, [])
    const onSuccessDelete = () => {
        setItemToDelete(null)
        funLoadData();
        toast.success("Bill deleted successfully", { position: "top-center" });
        // navigate(linksUrl.master.report.OutstandingReport);
    };


    const { mutate: deleteBills } = usePost({
        url: apiRoute.transactionEntry.deleteOutstandingBill,
        queryKey: "deleteOutstandingBill",
        onSuccessTasks: onSuccessDelete,
        successMessage: "Outstanding Bill deleted successfully!",
    });

    const totalGrossAmtD = useMemo(
        () =>
            calculateTotal({
                data: billsData,
                columnName: "GrossAmtD",
            }),
        [billsData]
    )

    const totalGrossAmt = useMemo(
        () =>
            calculateTotal({
                data: billsData,
                columnName: "GrossAmt",
            }),
        [billsData]
    )
    const totalNetAmt = useMemo(
        () =>
            calculateTotal({
                data: billsData,
                columnName: "NetAmt",
            }),
        [billsData]
    )
    const totalNetAmtD = useMemo(
        () =>
            calculateTotal({
                data: billsData,
                columnName: "NETAMTD",
            }),
        [billsData]
    )
    // Delete bill mutation would go here (similar to deletePurity in reference)
    // You'll need to implement this based on your API

    const handleDeleteClick = (bill) => {
        if (!bill?.Bill_No || !bill?.TransactionID) {
            toast.error("Error: Bill information is incomplete.");
            return;
        }
        setItemToDelete(bill)
        //setSelectedBill(bill);
       // setIsConfirmOpen(true);
    };

    
    const handleConfirmBox = (confirm) => {
        if (confirm && itemToDelete) {
              const payload = {
                db_user: user?.db_user,
                CompCode: compCode,
                ac_year: 2,
                TransactionID: itemToDelete?.TransactionID,
                Bill_Type: itemToDelete?.Bill_Type
            };

            deleteBills({ data: payload });
        }
        else
        setItemToDelete(null)
    };
    // const handleConfirmBox = (confirm) => {
    //     if (confirm && selectedBill) {
    //         const payload = {
    //             db_user: user?.db_user,
    //             CompCode: compCode,
    //             ac_year: 2,
    //             TransactionID: selectedBill?.TransactionID,
    //             Bill_Type: selectedBill?.Bill_Type
    //         };

    //         deletePurity({ data: payload });
    //     }

    //     setIsConfirmOpen(false);
    //     setSelectedBill(null);
    // };


    const handleAddNew = () => {
        dispatch(setNewEntryMode(true));
        dispatch(setDetailParams([]));
        navigate(linksUrl.outstandingBillEntry.createBillEntry);
    };

    const exportColumn = [
        {
            uid: "Bill_Type_Name",
            name: "Bill Type"
        },
        {
            uid: "Trade_name",
            name: "Trade"
        }, {
            uid: "Ac_Desc",
            name: "Account"
        },
        { uid: "Bill_Date", name: "Bill Date" },
        { uid: "Bill_No", name: "Bill No" },
        { uid: "GrossAmtD", name: "Gross Amt($)" },
        { uid: "GrossAmt", name: "Gross Amt" },
        { uid: "NETAMTD", name: "Net Amt($)" },
        { uid: "NetAmt", name: "Net Amt" },
        { uid: "Br_Desc", name: "Broker" }
    ]
    
    const downloadDataFooter = {
        Bill_Type_Name: "",
        Trade_name: "",
        Ac_Desc: "",
        Bill_Date: "",
        Bill_No: "",
        GrossAmtD: "Total: " + totalGrossAmtD,
        GrossAmt: "Total: " + totalGrossAmt,
        NETAMTD: "Total: " + totalNetAmtD,
        NetAmt: "Total: " + totalNetAmt,
        Br_Desc: "",
    }

    const columns = [
        { name: "Actions", uid: "actions", align: COLUMN_ALIGN_TYPE.center },
        { name: "Bill Type", uid: "Bill_Type_Name", sortable: true, align: COLUMN_ALIGN_TYPE.center },
        { name: "Trade", uid: "Trade_name", sortable: true, align: COLUMN_ALIGN_TYPE.center },
        { name: "Account", uid: "Ac_Desc", sortable: true, align: COLUMN_ALIGN_TYPE.center },
        { name: "Party", uid: "PAc_Desc", sortable: true, align: COLUMN_ALIGN_TYPE.center },
        { name: "Bill Date", uid: "Bill_Date", sortable: true, align: COLUMN_ALIGN_TYPE.center, type: COLUMN_TYPE.date },
        { name: "Bill No", uid: "Bill_No", sortable: true, align: COLUMN_ALIGN_TYPE.center },
        {
            name: "Gross Amount ($)", uid: "GrossAmtD", sortable: true, align: COLUMN_ALIGN_TYPE.center
            , type: COLUMN_TYPE.number,
            footer: {
                type: FOOTER_TYPE.text,
                label: "Total: ",
                value: totalGrossAmtD,
            },
        },
        {
            name: "Gross Amount", uid: "GrossAmt", sortable: true, align: COLUMN_ALIGN_TYPE.center,
            type: COLUMN_TYPE.number,
            footer: {
                type: FOOTER_TYPE.text,
                label: "Total: ",
                value: totalGrossAmt,
            },
        },
        {
            name: "Net Amount ($)", uid: "NETAMTD", sortable: true, align: COLUMN_ALIGN_TYPE.center,
            type: COLUMN_TYPE.number,
            footer: {
                type: FOOTER_TYPE.text,
                label: "Total: ",
                value: totalNetAmtD,
            },
        },
        {
            name: "Net Amount", uid: "NetAmt", sortable: true, align: COLUMN_ALIGN_TYPE.center,
            type: COLUMN_TYPE.number,
            footer: {
                type: FOOTER_TYPE.text,
                label: "Total: ",
                value: totalNetAmt,
            },
        },
        { name: "Broker", uid: "Br_Desc", sortable: true, align: COLUMN_ALIGN_TYPE.center },
    ];

    const rowActionBtn = [
        {
            icon: <FontAwesomeIcon icon={faEdit} style={{ color: "#379906" }} />,
            onClick: (row) => {
                dispatch(setDetailParams(row));
                dispatch(setNewEntryMode(false))
                navigate(linksUrl.outstandingBillEntry.createBillEntry);
            },
            type: ROW_ACTION_BTN_TYPE.link,
            tooltip: "Edit"
        },
        {
            icon: <FontAwesomeIcon icon={faTrash} style={{ color: "#c40c18" }} />,
            onClick: (row) => handleDeleteClick(row),
            type: ROW_ACTION_BTN_TYPE.link,
            tooltip: "Delete"
        },
    ];

    if (isPending) { return <LoadingCard /> };
    if (isError) { return <ErrorCard /> };



    return (
        <div>
            <MainContainer pageName="Outstanding Bills" breadcrumbs={["Transactions", "Outstanding Bills"]}>
                <div className="text-right mb-4">
                    <Button
                        size="sm"
                        color="primary"
                        startContent={<FontAwesomeIcon icon={faPlus} />}
                        onPress={handleAddNew}
                    >
                        Add New Bill
                    </Button>
                </div>
                <NextUITable
                    INITIAL_VISIBLE_COLUMNS={[
                        "actions",
                        "Bill_Type_Name",
                        "Trade_name",
                        "Ac_Desc",
                        "Bill_Date",
                        "Bill_No",
                        "GrossAmtD",
                        "GrossAmt",
                        "NETAMTD",
                        "NetAmt",
                        "Br_Desc"
                    ]}
                    data={billsData}
                    columns={columns}
                    rowActionBtn={rowActionBtn}
                    exportColumns={exportColumn}
                    downloadDataFooter={downloadDataFooter}
                    downloadFileName="Outstanding-Bills"
                    id="Bill_No"
                    searchPlaceholder="Search bills by account, party, bill no..."
                    showSearch={true}
                    showColumnsControl={true}
                    showPagination={true}
                    showAdvanceFilter={false}
                />
            </MainContainer>

{itemToDelete && itemToDelete!="" && <ConfirmBox handleConfirmBox={handleConfirmBox} />}
            {/* {isConfirmOpen && (
                <ConfirmBox
                    handleConfirmBox={handleConfirmBox}
                    message={`Are you sure you want to delete bill ${selectedBill?.Bill_No}?`}
                />
            )} */}
        </div>
    );
};

export default OutstandingBills